import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ShieldCheck, XCircle, Send, Save, History, Loader2, UserCheck, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail, useAuth } from "../../lib/auth";

const APPROVER_ROLES = new Set(["reviewer", "enhanced_reviewer", "admin", "super_admin"]);
const CREATOR_ROLES = new Set([
  "content_operator", "ecosystem_operator",
  "reviewer", "enhanced_reviewer", "admin", "super_admin",
]);
const PROVENANCE = ["BTC_PROVIDED", "BTC_APPROVED", "PARTNER_SUPPORTED", "NOT_CURRENTLY_AVAILABLE"];
const AVAILABILITY = ["CURRENTLY_AVAILABLE", "IN_DEVELOPMENT", "NOT_CURRENTLY_AVAILABLE", "FUTURE_ASPIRATIONAL"];
const READABILITY = ["", "A2", "B1", "B2"];
const REVIEW_STATUS = ["not_reviewed", "reviewed"];

const STATE_STYLES = {
  DRAFT: "bg-slate-100 text-slate-700",
  REVIEW: "bg-amber-100 text-amber-800",
  APPROVED: "bg-emerald-100 text-emerald-800",
  REJECTED: "bg-rose-100 text-rose-800",
};

function fmt(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" }); }
  catch { return "—"; }
}

export default function ContentGovernanceDetail() {
  const { recordId } = useParams();
  const { user } = useAuth();
  const [row, setRow] = useState(null);
  const [history, setHistory] = useState([]);
  const [eligibility, setEligibility] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [rejectReason, setRejectReason] = useState("");
  const [showReject, setShowReject] = useState(false);

  const [edit, setEdit] = useState({
    provenance: "",
    availability: "",
    readability_level: "",
    accessibility_review_status: "",
    inclusive_review_status: "",
    assumption_check_recorded: null,
    availability_verified_at: "",
    source_version: "",
    language: "",
  });

  const load = useCallback(async () => {
    setErr("");
    try {
      const [r, h] = await Promise.all([
        api.get(`/ea2/governance/${recordId}`),
        api.get(`/ea2/governance/${recordId}/history`),
      ]);
      setRow(r.data);
      setHistory(h.data.items || []);
      setEdit({
        provenance: r.data.provenance || "",
        availability: r.data.availability || "",
        readability_level: r.data.readability_level || "",
        accessibility_review_status: r.data.accessibility_review_status || "not_reviewed",
        inclusive_review_status: r.data.inclusive_review_status || "not_reviewed",
        assumption_check_recorded: !!r.data.assumption_check_recorded,
        availability_verified_at: r.data.availability_verified_at || "",
        source_version: r.data.source_version || "",
        language: r.data.language || "",
      });
      // Slice 3 — read-only patient eligibility lookup.  Never blocks
      // the page; failures render as an "unknown" eligibility block.
      try {
        const q = new URLSearchParams({
          source_version: r.data.source_version,
          language: r.data.language || "en",
        });
        const el = await api.get(
          `/content-governance/eligible/${r.data.content_class}/${encodeURIComponent(r.data.content_key)}?${q.toString()}`
        );
        setEligibility(el.data);
      } catch (_) {
        setEligibility({ eligible: false, reason: "SOURCE_NOT_FOUND" });
      }
    } catch (e) {
      setErr(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to load record.");
    }
  }, [recordId]);

  useEffect(() => { load(); }, [load]);

  const canEdit = row && ["DRAFT", "REJECTED", "APPROVED"].includes(row.lifecycle_state) && CREATOR_ROLES.has(user?.role);
  const canSubmit = row && row.lifecycle_state === "DRAFT" && CREATOR_ROLES.has(user?.role);
  const isCreator = row && user && row.creator_user_id === user.id;
  const canApprove = row && row.lifecycle_state === "REVIEW" && APPROVER_ROLES.has(user?.role) && !isCreator;
  const canReject = canApprove;

  const call = async (fn, okMsg) => {
    setBusy(true); setErr("");
    try { await fn(); if (okMsg) toast.success(okMsg); await load(); }
    catch (e) { const m = formatApiErrorDetail(e?.response?.data?.detail) || "Action failed."; setErr(m); toast.error(m); }
    finally { setBusy(false); }
  };

  const saveEdits = async () => {
    const payload = {};
    Object.entries(edit).forEach(([k, v]) => {
      if (v === "" || v === null) return;
      if (row && row[k] === v) return;
      payload[k] = v;
    });
    if (!Object.keys(payload).length) { toast.info("No changes to save."); return; }
    await call(() => api.post(`/ea2/governance/${recordId}/edit`, payload),
               "Governance metadata saved.");
  };

  if (!row) return (
    <div className="space-y-4">
      <Link to="/ops/content-governance" className="text-sm text-slate-500 inline-flex items-center gap-1" data-testid="cg-detail-back">
        <ArrowLeft className="w-4 h-4" /> Back to governance
      </Link>
      {err ? <div className="text-rose-700 text-sm" data-testid="cg-detail-error">{err}</div>
           : <div className="text-slate-500 text-sm">Loading…</div>}
    </div>
  );

  return (
    <div className="space-y-6" data-testid="cg-detail-page">
      <Link to="/ops/content-governance" className="text-sm text-slate-500 inline-flex items-center gap-1" data-testid="cg-detail-back">
        <ArrowLeft className="w-4 h-4" /> Back to governance
      </Link>

      {err && (
        <div className="border border-rose-300 bg-rose-50 text-rose-800 text-sm px-4 py-2 rounded" data-testid="cg-detail-error">
          {err}
        </div>
      )}

      {/* Identity + governance summary */}
      <section className="bg-white rounded-xl border p-5">
        <div className="flex flex-wrap items-start gap-3">
          <div className="flex-1 min-w-0">
            <div className="text-[11px] uppercase tracking-wider text-slate-500 font-bold">{row.content_class}</div>
            <h1 className="text-xl font-semibold text-slate-900 break-all" data-testid="cg-detail-key">{row.content_key}</h1>
            <div className="text-xs text-slate-500 mt-1">
              Source version <span className="font-mono">{row.source_version}</span> · Record v{row.version} · Language {row.language}
            </div>
          </div>
          <span className={`px-3 py-1 rounded-full text-xs font-semibold ${STATE_STYLES[row.lifecycle_state]}`} data-testid="cg-detail-state">
            {row.lifecycle_state}
          </span>
        </div>
        <dl className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-x-4 gap-y-2 text-xs">
          <div><dt className="uppercase text-slate-500">Provenance</dt><dd className="font-semibold text-slate-900">{row.provenance}</dd></div>
          <div><dt className="uppercase text-slate-500">Availability</dt><dd className="font-semibold text-slate-900">{row.availability}</dd></div>
          <div><dt className="uppercase text-slate-500">Availability verified at</dt><dd className="font-semibold text-slate-900">{row.availability_verified_at || "—"}</dd></div>
          <div><dt className="uppercase text-slate-500">Readability</dt><dd className="font-semibold text-slate-900">{row.readability_level || "—"}</dd></div>
          <div><dt className="uppercase text-slate-500">Accessibility review</dt><dd className="font-semibold text-slate-900">{row.accessibility_review_status}</dd></div>
          <div><dt className="uppercase text-slate-500">Inclusive review</dt><dd className="font-semibold text-slate-900">{row.inclusive_review_status}</dd></div>
          <div><dt className="uppercase text-slate-500">Assumption check</dt><dd className="font-semibold text-slate-900">{row.assumption_check_recorded ? "recorded" : "not recorded"}</dd></div>
          <div><dt className="uppercase text-slate-500">Created</dt><dd className="font-semibold text-slate-900">{fmt(row.created_at)}</dd></div>
          <div><dt className="uppercase text-slate-500">Updated</dt><dd className="font-semibold text-slate-900">{fmt(row.updated_at)}</dd></div>
          <div><dt className="uppercase text-slate-500">Last equity review</dt><dd className="font-semibold text-slate-900">{fmt(row.last_equity_review_at)}</dd></div>
          <div><dt className="uppercase text-slate-500">Next review</dt><dd className="font-semibold text-slate-900">{fmt(row.next_review_at)}</dd></div>
          <div><dt className="uppercase text-slate-500">Reviewer</dt><dd className="font-semibold text-slate-900">{row.reviewer_user_id ? row.reviewer_user_id.slice(0, 8) + "…" : "—"}</dd></div>
        </dl>
        {row.lifecycle_state === "APPROVED" && (
          <p className="mt-4 text-xs text-slate-500 border-t pt-3">
            Approving governance metadata does not publish the content, make it
            patient-visible, or authorize AI grounding. Those are separate
            future decisions.
          </p>
        )}
      </section>

      {/* Slice 3 — patient eligibility (read-only, derived) */}
      <section className="bg-white rounded-xl border p-5" data-testid="cg-detail-eligibility">
        <div className="flex items-start gap-3">
          {eligibility?.eligible ? (
            <UserCheck className="w-5 h-5 text-emerald-600 mt-0.5" />
          ) : (
            <ShieldAlert className="w-5 h-5 text-amber-600 mt-0.5" />
          )}
          <div className="flex-1">
            <h2 className="font-semibold text-slate-900">Patient eligibility</h2>
            <p className="text-xs text-slate-500 mt-1">
              Derived from governance metadata. Eligibility is not a lifecycle
              state — it is a live, deterministic predicate.
            </p>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <span
                data-testid="cg-eligibility-status"
                className={`px-3 py-1 rounded-full text-xs font-semibold ${
                  eligibility?.eligible
                    ? "bg-emerald-100 text-emerald-800"
                    : "bg-amber-100 text-amber-800"
                }`}
              >
                {eligibility?.eligible ? "Eligible" : "Not eligible"}
              </span>
              <span
                data-testid="cg-eligibility-reason"
                className="text-xs font-mono bg-slate-100 text-slate-700 px-2 py-1 rounded"
              >
                {eligibility?.reason || "—"}
              </span>
            </div>
            <p className="mt-3 text-xs text-slate-500">
              Eligible content may appear on a patient-facing surface that
              consumes the eligibility endpoint. It is not automatically
              published, translated, personalized, geographically matched, or
              grounded for Ask Northstar.
            </p>
          </div>
        </div>
      </section>

      {/* Edit metadata */}
      {canEdit && (
        <section className="bg-white rounded-xl border p-5" data-testid="cg-detail-edit">
          <h2 className="font-semibold text-slate-900 mb-3">Governance metadata</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
            <Select label="Provenance" testid="cg-edit-provenance" value={edit.provenance} onChange={(v) => setEdit({ ...edit, provenance: v })} options={PROVENANCE} />
            <Select label="Availability" testid="cg-edit-availability" value={edit.availability} onChange={(v) => setEdit({ ...edit, availability: v })} options={AVAILABILITY} />
            <label className="flex flex-col gap-1">
              <span className="text-slate-500">Availability verified at</span>
              <input
                type="text" data-testid="cg-edit-verified-at"
                value={edit.availability_verified_at}
                onChange={(e) => setEdit({ ...edit, availability_verified_at: e.target.value })}
                placeholder="2026-02-21T00:00:00+00:00"
                className="border rounded px-3 py-2"
              />
            </label>
            <Select label="Readability level" testid="cg-edit-readability" value={edit.readability_level} onChange={(v) => setEdit({ ...edit, readability_level: v })} options={READABILITY} allowEmpty />
            <Select label="Accessibility review" testid="cg-edit-access-status" value={edit.accessibility_review_status} onChange={(v) => setEdit({ ...edit, accessibility_review_status: v })} options={REVIEW_STATUS} />
            <Select label="Inclusive review" testid="cg-edit-inclusive-status" value={edit.inclusive_review_status} onChange={(v) => setEdit({ ...edit, inclusive_review_status: v })} options={REVIEW_STATUS} />
            <label className="inline-flex items-center gap-2 md:col-span-3">
              <input
                type="checkbox" data-testid="cg-edit-assumption"
                checked={!!edit.assumption_check_recorded}
                onChange={(e) => setEdit({ ...edit, assumption_check_recorded: e.target.checked })}
              />
              <span>Assumption check recorded</span>
            </label>
          </div>
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <button
              data-testid="cg-edit-save"
              onClick={saveEdits} disabled={busy}
              className="btc-btn-primary text-sm inline-flex items-center gap-2"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save metadata
            </button>
            {row.lifecycle_state === "APPROVED" && (
              <span className="text-xs text-amber-700">
                Material edits (source version, provenance, availability, language) will route this record back to DRAFT for re-review.
              </span>
            )}
          </div>
        </section>
      )}

      {/* Lifecycle actions */}
      <section className="bg-white rounded-xl border p-5" data-testid="cg-detail-actions">
        <h2 className="font-semibold text-slate-900 mb-3">Lifecycle actions</h2>
        <div className="flex flex-wrap items-center gap-2">
          {canSubmit && (
            <button
              data-testid="cg-action-submit" disabled={busy}
              onClick={() => call(() => api.post(`/ea2/governance/${recordId}/submit-for-review`), "Submitted for review.")}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-slate-300 text-sm hover:bg-slate-50"
            >
              <Send className="w-4 h-4" /> Submit for review
            </button>
          )}
          {canApprove && (
            <button
              data-testid="cg-action-approve" disabled={busy}
              onClick={() => call(() => api.post(`/ea2/governance/${recordId}/approve`), "Approved.")}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-600 text-white text-sm hover:bg-emerald-700"
            >
              <ShieldCheck className="w-4 h-4" /> Approve
            </button>
          )}
          {canReject && (
            <button
              data-testid="cg-action-reject-open" disabled={busy}
              onClick={() => setShowReject((v) => !v)}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-rose-300 text-rose-700 text-sm hover:bg-rose-50"
            >
              <XCircle className="w-4 h-4" /> Reject
            </button>
          )}
          {!canSubmit && !canApprove && !canReject && (
            <span className="text-xs text-slate-500" data-testid="cg-actions-none">
              No lifecycle actions available for your role at this state
              {isCreator && row.lifecycle_state === "REVIEW" ? " (creator ≠ approver)." : "."}
            </span>
          )}
        </div>
        {showReject && (
          <div className="mt-3 flex flex-wrap items-start gap-2" data-testid="cg-reject-form">
            <textarea
              value={rejectReason} onChange={(e) => setRejectReason(e.target.value)}
              data-testid="cg-reject-reason"
              placeholder="Rejection reason (required)"
              rows={2}
              className="flex-1 min-w-[240px] border rounded px-3 py-2 text-sm"
            />
            <button
              data-testid="cg-action-reject-confirm"
              disabled={busy || !rejectReason.trim()}
              onClick={() =>
                call(() => api.post(`/ea2/governance/${recordId}/reject`, { reason: rejectReason.trim() }),
                     "Rejected.").then(() => { setShowReject(false); setRejectReason(""); })
              }
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-rose-600 text-white text-sm hover:bg-rose-700 disabled:opacity-50"
            >
              Confirm rejection
            </button>
          </div>
        )}
      </section>

      {/* History */}
      <section className="bg-white rounded-xl border overflow-hidden" data-testid="cg-detail-history">
        <div className="px-4 py-3 border-b flex items-center gap-2 text-slate-900 font-medium">
          <History className="w-4 h-4" /> Governance history
        </div>
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500">
            <tr>
              <th className="text-left px-4 py-2">When</th>
              <th className="text-left px-4 py-2">Event</th>
              <th className="text-left px-4 py-2">Actor</th>
              <th className="text-left px-4 py-2">Summary</th>
            </tr>
          </thead>
          <tbody>
            {history.length === 0 && (
              <tr><td colSpan={4} className="px-4 py-6 text-center text-slate-500">No history yet.</td></tr>
            )}
            {history.map((e) => (
              <tr key={e.id} className="border-t">
                <td className="px-4 py-3 text-xs text-slate-500">{fmt(e.occurred_at)}</td>
                <td className="px-4 py-3"><span className="text-xs bg-slate-100 px-2 py-0.5 rounded">{e.event_type}</span></td>
                <td className="px-4 py-3 text-xs text-slate-600">{e.actor_role_at_time}</td>
                <td className="px-4 py-3 text-xs text-slate-800">{e.change_summary}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </div>
  );
}

function Select({ label, value, onChange, options, testid, allowEmpty = false }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-slate-500">{label}</span>
      <select
        data-testid={testid}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="border rounded px-3 py-2"
      >
        {allowEmpty && <option value="">—</option>}
        {options.filter((o) => allowEmpty || o !== "").map((o) => <option key={o}>{o}</option>)}
      </select>
    </label>
  );
}
